import { Injectable } from '@angular/core';

/**
 * Lightweight registry that lets a component (e.g. the Froala editor) expose a
 * handler which decides whether it is safe to navigate away. The
 * UnsavedChangesGuard consults this handler when the user tries to leave the
 * route (side menu, browser back, etc.), so the same "unsaved changes" prompt
 * used for page switch / add / exit also runs for router navigation.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
  private guardHandler: ((context?: string) => Promise<boolean>) | null = null;

  /**
   * Register the handler that resolves to true when it is safe to leave.
   */
  registerGuard(handler: (context?: string) => Promise<boolean>): void {
    this.guardHandler = handler;
  }

  /**
   * Remove a previously registered handler. When a specific handler is passed
   * it is only cleared if it is still the active one (avoids a newly mounted
   * component's handler being cleared by an old component's teardown).
   */
  clearGuard(handler?: (context?: string) => Promise<boolean>): void {
    if (!handler || this.guardHandler === handler) {
      this.guardHandler = null;
    }
  }

  /**
   * Resolves to true when navigation may proceed. Defaults to true when no
   * handler is registered.
   */
  async canLeave(context?: string): Promise<boolean> {
    if (!this.guardHandler) {
      return true;
    }
    return this.guardHandler(context);
  }
}
